import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { UserCircle, Camera, Edit, Save, X, Folder, StickyNote, CheckCircle, Lock, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PasswordInput } from '../../components/ui/password-input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { compressImageSafe, uploadErrorMessage } from '../../utils/imageCompression';
import { useUnreadNotes } from '../../contexts/UnreadNotesContext';

import { getBackendUrl } from '../../utils/getBackendUrl';
import { NotificationSettings } from '../../components/NotificationSettings';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ClientProfile = ({ user, onUpdate, isViewMode = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [projects, setProjects] = useState([]);
  const [clientNotes, setClientNotes] = useState([]);
  const { triggerRefresh } = useUnreadNotes();
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    address: user.address || '',
    company: user.company || '',
    industry: user.industry || '',
    billing_address: user.billing_address || ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchProjects();
    fetchClientNotes();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      // Filter projects for this specific client using assigned_clients array
      const clientProjects = response.data.filter(project => 
        project.assigned_clients && project.assigned_clients.includes(user.id)
      );
      setProjects(clientProjects);
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  const fetchClientNotes = async () => {
    try {
      // If in view mode, fetch notes for the specific user
      const endpoint = isViewMode ? `${API}/user-notes?user_id=${user.id}` : `${API}/user-notes`;
      const response = await axios.get(endpoint);
      setClientNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch user notes');
    }
  };

  const handleMarkNoteRead = async (noteId) => {
    try {
      await axios.post(`${API}/user-notes/${noteId}/mark-read`);
      toast.success('Note marked as read');
      fetchClientNotes();
      triggerRefresh(); // Update badge count
    } catch (error) {
      toast.error('Failed to mark note as read');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/user-notes/${noteId}`);
      toast.success('Note deleted successfully');
      fetchClientNotes();
      triggerRefresh(); // Update badge count
    } catch (error) {
      toast.error('Failed to delete note');
      console.error('Error deleting note:', error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/profile`, formData);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      await axios.put(`${API}/users/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setIsChangingPassword(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleUploadPicture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      toast.info('Compressing image...');
      const compressedFile = await compressImageSafe(file, 512, 512, 0.85);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      await axios.post(`${API}/users/upload-profile-picture`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated!');
      onUpdate();
    } catch (error) {
      toast.error(uploadErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-green-500/20 rounded-lg">
            <UserCircle className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Client Profile</h2>
            <p className="text-slate-400">View your projects and account information</p>
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div className="glass-card p-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Avatar Section */}
          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              <Avatar className="w-32 h-32">
                <AvatarImage src={user.profile_picture ? `${BACKEND_URL}${user.profile_picture}` : undefined} />
                <AvatarFallback className="bg-green-500 text-white text-3xl">
                  {user.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <label
                htmlFor="client-profile-picture"
                className="absolute bottom-0 right-0 bg-green-500 p-2 rounded-full cursor-pointer hover:bg-green-600 transition-colors"
              >
                <Camera className="w-4 h-4 text-white upload-icon-idle" />
                <input
                  id="client-profile-picture"
                  type="file"
                  accept="image/*"
                  onChange={handleUploadPicture}
                  className="hidden"
                />
              </label>
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-1">{user.name}</h2>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-semibold">
                <UserCircle className="w-4 h-4" />
                Client
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="flex-1">
            {isEditing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-300">Contact Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-slate-300">Company Name</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Your Company"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="industry" className="text-slate-300">Industry</Label>
                    <Input
                      id="industry"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      placeholder="e.g., Real Estate"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-300">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address" className="text-slate-300">Contact Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="billing_address" className="text-slate-300">Billing Address</Label>
                  <Input
                    id="billing_address"
                    value={formData.billing_address}
                    onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                    placeholder="If different from contact address"
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="submit" className="bg-gradient-to-r from-green-500 to-green-600">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Email</p>
                    <p className="text-white font-semibold">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Company</p>
                    <p className="text-white font-semibold">{user.company || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Industry</p>
                    <p className="text-white font-semibold">{user.industry || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Phone</p>
                    <p className="text-white font-semibold">{user.phone || 'Not provided'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-slate-400 text-sm mb-1">Contact Address</p>
                  <p className="text-white font-semibold">{user.address || 'Not provided'}</p>
                </div>

                {user.billing_address && (
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Billing Address</p>
                    <p className="text-white font-semibold">{user.billing_address}</p>
                  </div>
                )}

                <Button
                  onClick={() => setIsEditing(true)}
                  className="bg-gradient-to-r from-green-500 to-green-600"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Projects Summary */}
      <div className="glass-card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white">Your Projects</h3>
          <div className="flex items-center gap-2">
            <Folder className="w-5 h-5 text-green-400" />
            <span className="text-2xl font-bold text-white">{projects.length}</span>
          </div>
        </div>
        
        {projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((project) => (
              <div key={project.id} className="p-4 bg-slate-800/50 rounded-lg">
                <h4 className="text-white font-semibold mb-1">{project.name}</h4>
                <p className="text-slate-400 text-sm mb-2 line-clamp-2">{project.description}</p>
                <span className={`status-badge status-${project.status.toLowerCase()}`}>
                  {project.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-400 text-center py-8">No projects assigned yet</p>
        )}
      </div>

      {/* Admin Notes Section */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-yellow-500/20 rounded-lg">
            <StickyNote className="w-5 h-5 text-yellow-400" />
          </div>
          <h3 className="text-xl font-bold text-white">Messages from Admin</h3>
          {clientNotes.filter(n => !n.is_read).length > 0 && (
            <span className="inline-flex items-center justify-center w-7 h-7 text-sm font-bold text-white bg-red-500 rounded-full">
              {clientNotes.filter(n => !n.is_read).length}
            </span>
          )}
        </div>

        {clientNotes.length > 0 ? (
          <div className="space-y-4">
            {clientNotes.map((note) => (
              <div
                key={note.id}
                className={`p-4 rounded-lg border ${
                  note.is_read
                    ? 'bg-slate-800/30 border-slate-700'
                    : 'bg-orange-500/10 border-orange-500/50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-white font-semibold mb-1">From: {note.created_by_name}</p>
                    <p className="text-slate-400 text-sm">
                      {new Date(note.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })} at {new Date(note.created_at).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!note.is_read && !isViewMode && (
                      <Button
                        size="sm"
                        onClick={() => handleMarkNoteRead(note.id)}
                        className="bg-gradient-to-r from-green-500 to-green-600"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark as Read
                      </Button>
                    )}
                    {note.is_read && !isViewMode && (
                      <span className="text-xs text-green-400 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Read
                      </span>
                    )}
                    {isViewMode && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteNote(note.id)}
                        className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-white whitespace-pre-wrap">{note.content}</p>
                {note.is_read && note.read_at && (
                  <p className="text-slate-500 text-xs mt-2">
                    Read on: {new Date(note.read_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })} at {new Date(note.read_at).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-400 text-center py-8">No messages yet</p>
        )}
      </div>

      {/* Password Change Section */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <Lock className="w-5 h-5 text-red-400" />
          </div>
          <h3 className="text-xl font-bold text-white">Change Password</h3>
        </div>

        {!isChangingPassword ? (
          <Button
            onClick={() => setIsChangingPassword(true)}
            className="bg-gradient-to-r from-red-500 to-red-600"
          >
            <Lock className="w-4 h-4 mr-2" />
            Change Password
          </Button>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="current_password" className="text-slate-300">Current Password</Label>
              <PasswordInput
                id="current_password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="new_password" className="text-slate-300">New Password</Label>
              <PasswordInput
                id="new_password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="confirm_password" className="text-slate-300">Confirm New Password</Label>
              <PasswordInput
                id="confirm_password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                minLength={6}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-gradient-to-r from-red-500 to-red-600">
                <Save className="w-4 h-4 mr-2" />
                Update Password
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                  });
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Notification Settings */}
      <NotificationSettings user={user} />
    </div>
  );
};

export default ClientProfile;